import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

interface StatusPillProps {
  connected: boolean;
  /** Account is connected but flagged inactive/needs re-auth — shows an amber pulse instead of the green check. */
  needsAttention?: boolean;
  showLabel?: boolean;
  tooltipText?: string;
  className?: string;
}

/**
 * Compact connection indicator dot/badge with tooltip support.
 */
export const StatusPill: React.FC<StatusPillProps> = ({
  connected,
  needsAttention,
  showLabel = false,
  tooltipText,
  className,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const defaultLabel = needsAttention
    ? 'Needs Attention: Re-authorization required'
    : connected
      ? 'Connected & Active: Webhook operational'
      : 'Not Connected: Click Connect to link';

  const content = tooltipText || defaultLabel;

  return (
    <Tooltip content={content} placement="top">
      <span
        className={cn('inline-flex items-center gap-1 text-xs font-medium cursor-pointer', className)}
        aria-label={content}
      >
        <span className="relative inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center">
          <AnimatePresence mode="wait" initial={false}>
            {needsAttention ? (
              <motion.span
                key="attention"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ duration: 0.2 }}
                className="relative flex h-3.5 w-3.5 items-center justify-center rounded-full bg-amber-500 text-white shadow-2xs"
              >
                {!prefersReducedMotion && (
                  <motion.span
                    className="absolute inline-flex h-full w-full rounded-full bg-amber-400"
                    animate={{ opacity: [0.7, 0.15, 0.7] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  />
                )}
                <AlertCircle className="relative h-2.5 w-2.5" strokeWidth={3} />
              </motion.span>
            ) : connected ? (
              <motion.span
                key="on"
                initial={{ scale: 0, rotate: -45 }}
                animate={{ scale: 1, rotate: 0 }}
                exit={{ scale: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 22 }}
                className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 text-white shadow-2xs"
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </motion.span>
            ) : (
              <motion.span
                key="off"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ duration: 0.15 }}
                className="h-2 w-2 rounded-full bg-slate-300 dark:bg-slate-600"
              />
            )}
          </AnimatePresence>
        </span>
        {showLabel && (
          <span
            className={cn(
              needsAttention
                ? 'text-amber-600 dark:text-amber-400'
                : connected
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-slate-400'
            )}
          >
            {needsAttention ? 'Attention' : connected ? 'Connected' : 'Offline'}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

export default StatusPill;
